import { useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PublishCelebrationProps {
  publicUrl: string;
  onDismiss: () => void;
  // Site-lifecycle #19, Task 1: a page publish no longer implies the SITE
  // is live — publishing a page on a still-draft (not yet launched) site
  // must not claim "your site is up." Defaults true so site-detail.tsx's
  // own launch celebration (only ever rendered after a real launch) is
  // unchanged. The builder (page-edit.tsx) passes the SITE's actual status
  // and shows the honest "saved, not yet launched" copy when it's false.
  launched?: boolean;
}

export function PublishCelebration({ publicUrl, onDismiss, launched = true }: PublishCelebrationProps) {
  const [copied, setCopied] = useState(false);
  const displayUrl = publicUrl.replace(/^https?:\/\//, '');

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
    } catch {
      // older browsers, ignore — link is visible inline anyway
    }
  }

  if (!launched) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={
          'relative rounded-xl border border-border bg-surface-sunken p-5 sm:p-6 ' +
          '[animation:panel-enter_280ms_cubic-bezier(0.16,1,0.3,1)_both]'
        }
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-surface-sunken hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="space-y-1 pr-8">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Saved
          </p>
          <p className="text-sm text-foreground">
            Page saved to your site — launch the site when you're ready.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'relative rounded-xl border border-brand-200 bg-brand-50 p-5 sm:p-6 ' +
        '[animation:panel-enter_280ms_cubic-bezier(0.16,1,0.3,1)_both]'
      }
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="absolute right-3 top-3 rounded-md p-1 text-brand-700/70 transition-colors hover:bg-brand-100 hover:text-brand-700"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col gap-4 pr-8 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-brand-700">
            Now live
          </p>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            Your site is up.
          </h2>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block font-mono text-sm text-brand-700 underline-offset-4 hover:underline"
          >
            {displayUrl}
          </a>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} aria-live="polite">
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy link
              </>
            )}
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button size="sm">
              <ExternalLink className="h-4 w-4" />
              Open site
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
